import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Icon } from '../../components/Icon.jsx';
import Modal from '../../components/Modal.jsx';
import { useT, useLang, t as translate } from '../../lib/i18n.jsx';
import { get, post } from '../../lib/api.js';
import { useAsync } from '../../lib/hooks.js';
import { useAuth } from '../../lib/auth.jsx';
import { toast } from '../../lib/toast.jsx';
import { apiBase } from '../../lib/config.js';
import { errorMessage } from '../../lib/errors.js';
import { formatDate, formatMoney } from '../../lib/format.js';
import { invoiceDisplayStatus, invoiceStatusClass, invoiceStatusLabel, isOpenInvoice } from '../../lib/status.js';

export default function BillingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const t = useT();
  const { lang } = useLang();
  const { user } = useAuth();

  const { data: inv, loading, error } = useAsync(() => get(`/invoices/${id}`), [id]);
  const { data: hostings } = useAsync(() => get('/hostings').catch(() => []), []);
  const [payOpen, setPayOpen] = useState(false);

  const back = (
    <button className="back-link" onClick={() => navigate('/facturatie')}>
      <Icon name="arrow-left" size={14} />
      <span style={{ marginLeft: 4 }}>{t('bl.title')}</span>
    </button>
  );

  if (loading) {
    return (
      <main className="page">
        {back}
        <div className="page-loader">{t('common.loading')}</div>
      </main>
    );
  }
  if (error || !inv) {
    const notFound = !error || error.status === 404 || error.status === 403;
    return (
      <main className="page">
        {back}
        <div className="state-card" role={notFound ? undefined : 'alert'}>
          <h3>{notFound ? t('billingDetail.notFound') : t('billingDetail.loadFailed')}</h3>
          <p>
            {notFound
              ? t('billingDetail.notFound.body')
              : errorMessage(error, { lang })}
          </p>
        </div>
      </main>
    );
  }

  const hosting = inv.hosting_id ? (hostings || []).find((h) => h.id === inv.hosting_id) : null;
  const vatPct = inv.amount > 0 ? Math.round((inv.tax_amount / inv.amount) * 100) : null;
  const history = makeHistory(inv, lang);

  const downloadPdf = async () => {
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${apiBase()}/invoices/${inv.id}/download`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!r.ok) throw Object.assign(new Error(), { status: r.status });
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice_${inv.invoice_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast(e?.status ? errorMessage(e, { lang }) : t('billingDetail.downloadFailed'));
    }
  };

  return (
    <main className="page" data-screen-label={`Invoice ${inv.invoice_number}`}>
      {back}

      <div className="detail-head">
        <div className="fav-lg"><Icon name="card" size={20} /></div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h1 style={{ fontSize: 22 }}>{t('billingDetail.title', { number: inv.invoice_number })}</h1>
          <div style={{ display: 'flex', gap: 8, marginTop: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            <span className={'status-pill ' + invoiceStatusClass(inv)}>
              <span className="dot" />
              {invoiceStatusLabel(inv, lang)}
            </span>
            <span style={{ fontSize: 12.5, color: 'var(--ink-3)' }}>
              {formatDate(inv.issue_date, lang)} · {t('billingDetail.due', { date: formatDate(inv.due_date, lang) })}
            </span>
          </div>
        </div>
        <button className="btn-ghost" onClick={() => window.print()}>
          <Icon name="card" size={13} />
          <span style={{ marginLeft: 6 }}>{t('billingDetail.print')}</span>
        </button>
        <button className="btn-ghost" onClick={downloadPdf}>
          <Icon name="download" size={13} />
          <span style={{ marginLeft: 6 }}>PDF</span>
        </button>
        {isOpenInvoice(inv) && (
          <button className="btn-primary" onClick={() => setPayOpen(true)}>
            <Icon name="card" size={13} stroke={2} />
            <span style={{ marginLeft: 6 }}>{t('billingDetail.payNow')}</span>
          </button>
        )}
      </div>

      <div className="dash-grid">
        <section className="card">
          <div className="card-h"><h3>{t('billingDetail.items')}</h3></div>
          <div style={{ padding: '6px 22px 4px' }}>
            <div className="kv">
              <span className="k">{t('bd.items.col.desc')}</span>
              <span className="v">
                {hosting
                  ? `${hosting.package?.name ? t('billingDetail.hostingPlan', { plan: hosting.package.name }) : t('nav.hosting')} · ${hosting.domain}`
                  : inv.hosting_id
                    ? t('billingDetail.hostingId', { id: inv.hosting_id })
                    : t('billingDetail.services')}
              </span>
            </div>
            <div className="kv">
              <span className="k">{t('billingDetail.invoiceDate')}</span>
              <span className="v">{formatDate(inv.issue_date, lang)}</span>
            </div>
          </div>
          <div className="inv-totals">
            <div className="inv-totals__row">
              <span>{t('bd.items.subtotal')}</span>
              <span className="mono">{formatMoney(inv.amount, lang)}</span>
            </div>
            <div className="inv-totals__row">
              <span>{vatPct != null ? t('billingDetail.vatPct', { pct: vatPct }) : t('billingDetail.vat')}</span>
              <span className="mono">{formatMoney(inv.tax_amount, lang)}</span>
            </div>
            <div className="inv-totals__row inv-totals__row--total">
              <span>{t('bd.items.total')}</span>
              <span className="mono">{formatMoney(inv.total_amount, lang)}</span>
            </div>
          </div>
          <p style={{ padding: '0 22px 16px', margin: 0, fontSize: 12, color: 'var(--ink-4)' }}>
            {t('billingDetail.pdfNote')}
          </p>
        </section>

        <aside className="side-stack">
          <section className="card">
            <div className="card-h"><h3>{t('billingDetail.billedTo')}</h3></div>
            <div style={{ padding: '6px 22px 16px', fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.55 }}>
              <div style={{ fontWeight: 500, color: 'var(--ink)' }}>{user?.full_name || '—'}</div>
              <div className="mono" style={{ fontSize: 12 }}>{user?.email}</div>
              <div style={{ marginTop: 8, fontSize: 11.5, color: 'var(--ink-4)' }}>
                {t('billingDetail.addressNote')}
              </div>
            </div>
          </section>

          <section className="card">
            <div className="card-h"><h3>{t('billingDetail.history')}</h3></div>
            {history.map((h, i) => (
              <div className="act" key={i}>
                <span className={`ico ${h.ico}`}>
                  {h.ico === 'ok' && <Icon name="check" size={13} stroke={2} />}
                  {h.ico === 'warn' && <Icon name="alert" size={13} stroke={1.8} />}
                  {h.ico === 'acc' && <Icon name="card" size={13} stroke={1.8} />}
                </span>
                <span className="t">{h.text}</span>
                <span className="when">{h.when}</span>
              </div>
            ))}
          </section>
        </aside>
      </div>

      {payOpen && <PayModal invoice={inv} lang={lang} onClose={() => setPayOpen(false)} />}
    </main>
  );
}

// Built from the invoice's own dates only — no events we can't vouch for.
function makeHistory(inv, lang) {
  const out = [{ ico: 'acc', text: translate(lang, 'billingDetail.hist.created'), when: formatDate(inv.issue_date, lang) }];
  const s = invoiceDisplayStatus(inv);
  if (s === 'overdue') out.unshift({ ico: 'warn', text: translate(lang, 'billingDetail.hist.overdue'), when: formatDate(inv.due_date, lang) });
  if (s === 'failed') out.unshift({ ico: 'warn', text: translate(lang, 'invoiceStatus.failed'), when: '' });
  if (s === 'cancelled') out.unshift({ ico: 'warn', text: translate(lang, 'billingDetail.hist.cancelled'), when: '' });
  if (s === 'paid') out.unshift({ ico: 'ok', text: translate(lang, 'billingDetail.hist.paid'), when: inv.paid_at ? formatDate(inv.paid_at, lang) : '' });
  return out;
}

const METHODS = [
  // null lets Mollie's checkout offer every method that is enabled.
  { key: null, id: 'any' },
  { key: 'ideal', id: 'ideal' },
  { key: 'creditcard', id: 'creditcard' },
];

function PayModal({ invoice, lang, onClose }) {
  const t = useT();
  const [method, setMethod] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const pay = async () => {
    setBusy(true);
    setErr('');
    try {
      const res = await post('/payments', { invoice_id: invoice.id, method });
      if (res?.checkout_url) {
        window.location.href = res.checkout_url;
        return;
      }
      setErr(t('billingPage.noPaymentLink'));
    } catch (e) {
      setErr(errorMessage(e, { lang }));
    }
    setBusy(false);
  };

  return (
    <Modal
      onClose={onClose}
      maxWidth={520}
      title={t('billingDetail.pay.title')}
      description={`${invoice.invoice_number} · ${formatMoney(invoice.total_amount, lang)}`}
      footer={
        <>
          <button type="button" className="btn-ghost" onClick={onClose}>
            {t('g.cancel')}
          </button>
          <button type="button" className="btn-primary" disabled={busy} onClick={pay}>
            {busy ? t('billingPage.working') : t('billingDetail.pay.cta')}
          </button>
        </>
      }
    >
      <div className="modal-body">
        {err && <div className="login-error" role="alert">{err}</div>}
        <div id="pay-method-label" style={{ fontSize: 13, color: 'var(--ink-2)', marginBottom: 12 }}>
          {t('billingDetail.pay.choose')}
        </div>
        <div role="radiogroup" aria-labelledby="pay-method-label">
          {METHODS.map((m) => (
            <div
              key={m.id}
              className="opt"
              role="radio"
              tabIndex={method === m.key ? 0 : -1}
              aria-checked={method === m.key}
              onClick={() => setMethod(m.key)}
              onKeyDown={(e) => {
                if (e.key === ' ' || e.key === 'Enter') {
                  e.preventDefault();
                  setMethod(m.key);
                }
                if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                  e.preventDefault();
                  const i = METHODS.findIndex((x) => x.key === method);
                  const next = METHODS[(i + (e.key === 'ArrowDown' ? 1 : METHODS.length - 1)) % METHODS.length];
                  setMethod(next.key);
                  e.currentTarget.parentElement.children[METHODS.indexOf(next)]?.focus();
                }
              }}
            >
              <span className="radio" />
              <span>
                <div className="lbl">{t(`billingDetail.method.${m.id}`)}</div>
                <div className="sub">{t(`billingDetail.method.${m.id}.sub`)}</div>
              </span>
            </div>
          ))}
        </div>
      </div>
    </Modal>
  );
}
